import React, { useCallback, useMemo } from 'react';
import { Tooltip } from 'antd';
import { PieChartOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ChartEmptyState from './ChartEmptyState';
import { formatMoney } from '../utils/formatMoney';
import { truncateChartLabel } from '../utils/chartLabels';
import './AssistantsPieChart.css';

const COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#6366f1',
  '#ef4444',
  '#06b6d4',
  '#8b5cf6',
  '#ec4899',
];

function toSpendPieData(rows, { nameKey = 'model', amountKey = 'total_cost_usd' } = {}) {
  const sorted = [...(rows || [])]
    .filter((row) => (row[amountKey] || 0) > 0)
    .sort((a, b) => (b[amountKey] || 0) - (a[amountKey] || 0));
  const top = sorted.slice(0, 10);
  const total = top.reduce((sum, row) => sum + (row[amountKey] || 0), 0);
  if (total <= 0) return [];

  return top.map((row) => ({
    name: row[nameKey] || 'Unknown',
    value: Math.round(((row[amountKey] || 0) / total) * 1000) / 10,
    amount: row[amountKey] || 0,
  }));
}

export default function SpendPieChart({
  data = [],
  height = 220,
  nameKey = 'model',
  amountKey = 'total_cost_usd',
  emptyState = null,
}) {
  const chartData = useMemo(
    () => toSpendPieData(data, { nameKey, amountKey }),
    [data, nameKey, amountKey],
  );

  const customTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const color = payload[0].color || '#000';
    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip__header">
          <span className="chart-tooltip__swatch" style={{ background: color }} />
          <div className="chart-tooltip__title">{d.name}</div>
        </div>
        <div className="chart-tooltip__row">
          Spend: <strong>{formatMoney(d.amount)}</strong>
        </div>
        <div className="chart-tooltip__row">
          Share: <strong>{d.value}%</strong>
        </div>
      </div>
    );
  };

  const renderCustomLabel = useCallback(({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.08) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fontWeight={600}
        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)', pointerEvents: 'none' }}
      >
        {`${Math.round(percent * 100)}%`}
      </text>
    );
  }, []);

  if (emptyState) {
    return (
      <ChartEmptyState
        icon={PieChartOutlined}
        title={emptyState.title}
        description={emptyState.description}
        height={height}
        minHeight={160}
      />
    );
  }

  if (!chartData.length) return null;

  const chartHeight = Math.max(160, height - 72);

  return (
    <div className="spend-pie-chart" style={{ marginBottom: 16 }}>
      <div className="spend-pie-chart__plot" style={{ height: chartHeight, minHeight: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              dataKey="value"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={Math.max(56, Math.min(Math.floor(chartHeight * 0.38), 88))}
              innerRadius={Math.max(28, Math.min(Math.floor(chartHeight * 0.2), 44))}
              stroke="rgba(255,255,255,0.9)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip content={customTooltip} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="spend-pie-legend">
        {chartData.map((entry, index) => (
          <Tooltip
            key={`${entry.name}-${index}`}
            title={`${entry.name} · ${formatMoney(entry.amount)} (${entry.value}%)`}
          >
            <div className="spend-pie-legend__item">
              <span
                className="spend-pie-legend__swatch"
                style={{ background: COLORS[index % COLORS.length] }}
              />
              <span className="spend-pie-legend__label">{truncateChartLabel(entry.name, 24)}</span>
              <span className="spend-pie-legend__amount">{formatMoney(entry.amount)}</span>
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}
